import React, { createContext, useContext } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_SCRIPT_ID,
  isMapsConfigured,
} from '../config/maps';

/**
 * Loads the Google Maps JS API once for the whole ecommerce app. The
 * Places autocomplete on the checkout page reads `useMapsLoader()` to
 * decide whether to render the autocomplete-enabled input or the legacy
 * plain text input.
 */

interface MapsContextValue {
  isLoaded: boolean;
  loadError: Error | undefined;
  isConfigured: boolean;
}

const MapsContext = createContext<MapsContextValue>({
  isLoaded: false,
  loadError: undefined,
  isConfigured: false,
});

export const MapsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const configured = isMapsConfigured();
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: configured ? GOOGLE_MAPS_API_KEY : '',
    libraries: GOOGLE_MAPS_LIBRARIES,
    preventGoogleFontsLoading: true,
  });
  return (
    <MapsContext.Provider
      value={{ isLoaded: configured && isLoaded, loadError, isConfigured: configured }}
    >
      {children}
    </MapsContext.Provider>
  );
};

export function useMapsLoader(): MapsContextValue {
  return useContext(MapsContext);
}
